import { createAction } from '@reduxjs/toolkit';
import { RootDispatch, RootState, Thunk } from 'store/types';
import { getProfile } from 'store/profile/actions';
import { Profile } from 'store/profile/types';
import { createErrorAction } from 'store/utils';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import { profileConsumerSelector } from 'store/profile/selectors';
import { getCurrentSignedInUserPrimaryDoctor } from 'store/myDoctors/actions';
import Config from 'react-native-config';
import {
  DispatcherCategoryService,
  DispatcherAuthPrefix,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import { JsonObj } from '@mhp/general-interface';

export const switchAccountStart = createAction<string>('Account/SWITCH_ACCOUNT_STARTED');
export const switchAccountFail = createErrorAction('Account/SWITCH_ACCOUNT_FAILED');
export const switchAccountSuccess = createAction<Profile>('Account/SWITCH_ACCOUNT_SUCCESS');
export const setSwitchAccountOpen = createAction<boolean>('Account/SET_SWITCH_ACCOUNT_OPEN');

export const switchAccount = (
  consumerId: string,
  currUrl?: string,
  prevUrl?: string
): Thunk<JsonObj> => async (dispatch: RootDispatch) => {
  dispatch(switchAccountStart(consumerId));
  const getProfileResult = await dispatch(getProfile(consumerId));

  if (getProfileResult.error) {
    return dispatch(switchAccountFail(getProfileResult.error));
  }

  analyticsService.logEvent(AnalyticsEvent.AccountSwitched, {
    currentUrl: currUrl,
    referringUrl: prevUrl
  });

  const result = await dispatch(switchAccountSuccess(getProfileResult.payload.data));
  dispatch(getCurrentSignedInUserPrimaryDoctor());
  return result;
};

export const RESET_ACCESS = 'ACCOUNT.RESET_ACCESS';
export const RESET_ACCESS_SUCCESS = 'ACCOUNT.RESET_ACCOUNT_SUCCESS';
export const RESET_ACCESS_FAIL = 'ACCOUNT.RESET_ACCESS_FAIL';

export const resetAccountAccess = () => (dispatch: Function) => {
  return dispatch({
    type: RESET_ACCESS,
    payload: {
      client: 'tps',
      request: {
        method: 'delete',
        url: '/client/source'
      }
    }
  });
};

export const CREATE_MESSAGING_ALIASES = 'ACCOUNT.CREATE_MESSAGING_ALIASES';
export const CREATE_MESSAGING_ALIASES_SUCCESS = 'ACCOUNT.CREATE_MESSAGING_ALIASES_SUCCESS';
export const CREATE_MESSAGING_ALIASES_FAIL = 'CREATE_MESSAGING_ALIASES_FAIL';

export const createMessagingAliases = () => (dispatch: Function) => {
  const isExpansion = Config.CONSUMERS_EXPANSION_ENABLED === 'enabled';
  let url = '';

  if (isExpansion) {
    url = getDispatcherUrl(
      DispatcherCategoryService.CONSUMER,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.CONSUMERS,
      DispatcherEndpoint.ID_REFRESH
    );
  } else {
    url = '/consumer/messagingAliases';
  }

  return dispatch({
    type: CREATE_MESSAGING_ALIASES,
    payload: {
      client: isExpansion ? 'dispatcher' : 'profile',
      request: {
        method: 'post',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' }
      }
    }
  });
};

export const returnToMain = () => (dispatch: RootDispatch, getStore: () => RootState) => {
  const store = getStore();
  const consumer = profileConsumerSelector(store);
  return dispatch(switchAccount(consumer.consumerId));
};
